import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { MessagesSquare, Plus } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

interface FaqSectionProps {
    faqs: Array<Faq>
}

export const FaqSection: FC<FaqSectionProps> = ({ faqs }): ReactNode => {
    if (faqs.length === 0) return null

    return (
        <section id='faq' aria-labelledby='faq-title' className='border-t bg-card py-20 sm:py-28'>
            <Container className='grid gap-12 lg:grid-cols-[1fr_1.35fr] lg:gap-16'>
                <div className='lg:sticky lg:top-28 lg:self-start'>
                    <SectionHeading
                        id='faq-title'
                        eyebrow="FAQ"
                        title="Questions, answered"
                        description="Everything people usually ask before they join their first class."
                    />

                    <div className='mt-8 rounded-3xl border bg-background p-6'>
                        <span className='flex size-10 items-center justify-center rounded-xl bg-secondary text-primary'>
                            <MessagesSquare className='size-5' aria-hidden='true' />
                        </span>

                        <p className='mt-4 font-semibold'>
                            Still wondering about something?
                        </p>

                        <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                            Send your question with the enrollment form. There is no commitment, and an advisor replies within one working day.
                        </p>

                        <ButtonLink href='/enroll' variant='outline' size='sm' className='mt-5'>
                            Ask an advisor
                        </ButtonLink>
                    </div>
                </div>

                <div className='divide-y rounded-3xl border bg-background'>
                    {faqs.map(faq => (
                        <details key={faq.id} className='group px-5 sm:px-7 [&_summary::-webkit-details-marker]:hidden'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-left font-medium focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring sm:py-6'>
                                <span className='text-base sm:text-lg' data-mdlfy-cms={`faqs:${faq.id}:question`}>
                                    {faq.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className='flex size-8 shrink-0 items-center justify-center rounded-full border transition duration-200 group-open:rotate-45 group-open:border-primary group-open:bg-primary group-open:text-primary-foreground'
                                >
                                    <Plus className='size-4' />
                                </span>
                            </summary>

                            <p className='pr-6 pb-6 leading-relaxed text-muted-foreground sm:pr-14' data-mdlfy-cms={`faqs:${faq.id}:answer`}>
                                {faq.answer}
                            </p>
                        </details>
                    ))}
                </div>
            </Container>
        </section>
    )
}
